import { unref, ref, watch, onMounted } from 'vue'
import { useForm } from 'vee-validate'
import type { ContactFormData } from './contact-form.types'
import { contactFormRules } from './contact-form.rules'
import {
  phoneRequiredRule,
  phoneFormatRule,
  phoneMinLengthRule,
  phoneMaxLengthRule,
  phoneLengthRule,
} from '@/composables/phone/validation/usePhoneValidation'
import { useContactFormShared } from './useContactFormShared'
import { buildPhoneFormData, runContactSubmitFlow } from './contact-form.helpers'

const INITIAL_VALUES = {
  fullName: '',
  phone: '',
  locality: '',
  region: '',
  message: '',
  privacyAccepted: false,
}

export interface UseContactPhoneFormOptions {
  requirePrivacyAccepted?: boolean
  showRegion?: boolean
  courseId?: string
}

const fieldOpts = { validateOnModelUpdate: false }

function createPhoneValidator(
  isInitialized: { value: boolean },
  rules: Array<(value: string) => boolean | string>
) {
  return (value: unknown): boolean | string => {
    if (!isInitialized.value) return true
    const str = typeof value === 'string' ? value : String(value ?? '')
    for (const rule of rules) {
      const result = rule(str)
      if (result !== true) return result
    }
    return true
  }
}

export function useContactPhoneForm(options: UseContactPhoneFormOptions = {}) {
  const { requirePrivacyAccepted = true, showRegion: showRegionProp = false, courseId } = options
  const shared = useContactFormShared({ showRegion: showRegionProp })
  const isPhoneInitialized = ref(false)

  const phoneValidation = createPhoneValidator(isPhoneInitialized, [
    phoneRequiredRule,
    phoneFormatRule,
    phoneMinLengthRule,
    phoneLengthRule,
    phoneMaxLengthRule,
  ])

  const validationSchema = {
    fullName: contactFormRules.fullName,
    phone: phoneValidation,
    locality: () => true as const,
    region: (value: unknown) => (shared.showRegion.value ? contactFormRules.regionRequired(value) : true),
    message: contactFormRules.messageOptional,
    privacyAccepted: (value: unknown) =>
      requirePrivacyAccepted ? contactFormRules.privacyAccepted(value) : (true as const),
  }

  const { defineField, errors, validate, resetForm: veeResetForm, setErrors, values } = useForm({
    validationSchema,
    initialValues: { ...INITIAL_VALUES },
    validateOnMount: false,
  })

  const [fullName, fullNameAttrs] = defineField('fullName', fieldOpts)
  const [phone, phoneAttrs] = defineField('phone', fieldOpts)
  const [locality, localityAttrs] = defineField('locality', fieldOpts)
  const [region, regionAttrs] = defineField('region', fieldOpts)
  const [message, messageAttrs] = defineField('message', fieldOpts)
  const [privacyAccepted, privacyAcceptedAttrs] = defineField('privacyAccepted', fieldOpts)

  const initializePhone = () => {
    setTimeout(() => { isPhoneInitialized.value = true }, 100)
  }
  const resetForm = () => {
    veeResetForm({ values: { ...INITIAL_VALUES } })
    setErrors({})
  }

  const loading = ref(false)
  const submitError = ref<string | null>(null)

  const submitForm = async (
    onSuccess?: (data: ContactFormData) => void,
    onError?: (error: Error) => void,
    getErrorMessage?: (error: Error, statusCode?: number) => string
  ) => {
    await runContactSubmitFlow(
      validate,
      () => unref(values) as Record<string, unknown> | undefined,
      (v) =>
        buildPhoneFormData(v, {
          showRegion: shared.showRegion.value,
          courseId,
        }),
      shared.submitToApi,
      {
        setLoading: (v) => { loading.value = v },
        setError: (v) => { submitError.value = v },
        resetForm,
      },
      { onSuccess, onError, getErrorMessage }
    )
  }

  onMounted(initializePhone)

  return {
    fullName,
    fullNameAttrs,
    phone,
    phoneAttrs,
    locality,
    localityAttrs,
    region,
    regionAttrs,
    message,
    messageAttrs,
    privacyAccepted,
    privacyAcceptedAttrs,
    errors,
    showRegion: shared.showRegion,
    resetForm,
    initializePhone,
    submitForm,
    loading,
    submitError,
  }
}
